import React, { useState, useEffect } from 'react';
import { feedbackApi } from '../../services/api';

function FeedbackList() {
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [filters, setFilters] = useState({
    university: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });

  // Universities for dropdown
  const [universities, setUniversities] = useState([]);

  const perPage = 20;

  // Count active filters (excluding default sort)
  const activeFilterCount = [
    filters.university,
    filters.dateFrom,
    filters.dateTo,
  ].filter(v => v !== '').length;

  const fetchUniversities = async () => {
    try {
      const response = await feedbackApi.getUniversities();
      setUniversities(response.data);
    } catch (err) {
      console.error('Error loading universities:', err);
    }
  };

  const fetchFeedback = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        page,
        per_page: perPage,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
      };
      if (search) params.search = search;
      if (filters.university) params.university = filters.university;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await feedbackApi.list(params);
      setFeedback(response.data.feedback);
      setTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Feedbacks');
      console.error('Feedback error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [page, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchFeedback();
  };

  const handleOpenFilterPopover = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setShowFilterPopover(false);
  };

  const handleCancelFilters = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(false);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      university: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
    };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
    setPage(1);
  };

  const handleSortChange = (field) => {
    const newSortOrder = filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    setFilters({ ...filters, sortBy: field, sortOrder: newSortOrder });
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNpsColor = (score) => {
    if (score >= 9) return '#22c55e'; // Green - Promoter
    if (score >= 7) return '#eab308'; // Yellow - Passive
    return '#ef4444'; // Red - Detractor
  };

  const getNpsLabel = (score) => {
    if (score >= 9) return 'Promoter';
    if (score >= 7) return 'Passiv';
    return 'Kritiker';
  };

  const totalPages = Math.ceil(total / perPage);

  // Calculate NPS statistics
  const promoters = feedback.filter(f => f.nps_score >= 9).length;
  const passives = feedback.filter(f => f.nps_score >= 7 && f.nps_score < 9).length;
  const detractors = feedback.filter(f => f.nps_score < 7).length;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Feedback</h1>
        <p className="page-subtitle">{total} Feedbacks insgesamt</p>
      </div>

      {/* NPS Summary */}
      {feedback.length > 0 && (
        <div className="stats-cards" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ borderLeft: '4px solid #22c55e' }}>
            <div className="stat-value">{promoters}</div>
            <div className="stat-label">Promoter (9-10)</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #eab308' }}>
            <div className="stat-value">{passives}</div>
            <div className="stat-label">Passiv (7-8)</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
            <div className="stat-value">{detractors}</div>
            <div className="stat-label">Kritiker (1-6)</div>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Feedback-Text durchsuchen..."
            className="search-input"
          />
          <button type="submit" className="search-button">Suchen</button>
        </form>

        <div className="filter-controls">
          <button
            type="button"
            className="filter-button"
            onClick={handleOpenFilterPopover}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="reset-filter-button"
              onClick={handleResetFilters}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Filter Popover */}
      {showFilterPopover && (
        <div className="filter-popover-overlay" onClick={handleCancelFilters}>
          <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popover-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="filter-title">Filter</span>
            </div>

            <div className="filter-popover-content">
              {/* University Filter */}
              <div className="filter-section">
                <label className="filter-section-label">Hochschule</label>
                <select
                  className="filter-input"
                  value={tempFilters.university}
                  onChange={(e) => setTempFilters({ ...tempFilters, university: e.target.value })}
                >
                  <option value="">Alle Hochschulen</option>
                  {universities.map((uni) => (
                    <option key={uni} value={uni}>{uni}</option>
                  ))}
                </select>
              </div>

              {/* Date Range Filter */}
              <div className="filter-section">
                <label className="filter-section-label">Zeitraum</label>
                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label className="date-label">Von</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateFrom}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="date-input-group">
                    <label className="date-label">Bis</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.dateTo}
                      onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="filter-popover-buttons">
                <button
                  type="button"
                  className="filter-cancel-button"
                  onClick={handleCancelFilters}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="filter-apply-button"
                  onClick={handleApplyFilters}
                >
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th
                    className="sortable-header"
                    onClick={() => handleSortChange('nps_score')}
                    style={{ cursor: 'pointer' }}
                  >
                    NPS Score
                    {filters.sortBy === 'nps_score' && (
                      <span className="sort-indicator">
                        {filters.sortOrder === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </th>
                  <th>Benutzer</th>
                  <th>E-Mail</th>
                  <th>Hochschule</th>
                  <th>Feedback</th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSortChange('created_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Datum
                    {filters.sortBy === 'created_at' && (
                      <span className="sort-indicator">
                        {filters.sortOrder === 'desc' ? ' ↓' : ' ↑'}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {feedback.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-table-message">
                      {activeFilterCount > 0 ? 'Keine Feedbacks gefunden mit den gewählten Filtern' : 'Keine Feedbacks vorhanden'}
                    </td>
                  </tr>
                ) : (
                  feedback.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '2rem',
                              height: '2rem',
                              borderRadius: '50%',
                              backgroundColor: getNpsColor(item.nps_score),
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                            }}
                          >
                            {item.nps_score}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {getNpsLabel(item.nps_score)}
                          </span>
                        </div>
                      </td>
                      <td>{item.user_name}</td>
                      <td>{item.user_email}</td>
                      <td>{item.enrolled_university || '-'}</td>
                      <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {item.feedback_text || '-'}
                      </td>
                      <td>{formatDate(item.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                Zurück
              </button>
              <span className="pagination-info">
                Seite {page} von {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Weiter
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FeedbackList;
